import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useProductNavigation } from '../../hooks/useProductNavigation';
import ProductCard from './ProductCard';
import BestSellersMobile from './BestSellers.Mobile';
import bestSellerService from '../../../pages/index/services/bestseller.service';
import LoadingSpinner from '../LodingSpiner/LodingSpiner';
import { BestSellerProduct } from '../../../pages/index/dto/bestseller.dto';
import { useCart } from '../../../contexts/CartContext';
import { useWishlist } from '../../../pages/wishlist/WishlistContext';
import VariantSheetDesktop from '../VariantSheetDesktop/VariantSheetDesktop';
import VariantOptionsModal from '../VariantOptionModal/VariantOptionsModal';
import VariantOptionModalMobile from '../VariantOptionModal/VariantOptionModalMobile';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/BestSellers.css';
import './styles/BestSellers.Mobile.css';

const convertVariantsForDisplay = (product: BestSellerProduct) => {
  if (!product.variants || product.variants.length === 0) {
    return [{
      id: '',
      size: product.title,
      price: product.price,
      compareAtPrice: product.compareAtPrice,
      rollLength: 1,
      attributes: {},
      variantImage: undefined,
      variantImages: [],
    }];
  }
  return product.variants.map((variant: any) => {
    let variantDisplayName = variant.variantTitle || variant.sku;
    if (variant.attributes) {
      const attributeValues = Object.values(variant.attributes);
      if (attributeValues.length > 0) {
        variantDisplayName = attributeValues[0] as string;
      }
    }
    const baseVariant = {
      id: variant.sku || variant._id,
      size: variantDisplayName,
      price: variant.price || product.price,
      compareAtPrice: variant.compareAtPrice,
      pricePerMeter: (variant.price || product.price) / (variant.weight || 1),
      rollLength: variant.weight || 90,
      attributes: variant.attributes || {},
      variantImage: variant.variantImages?.[0] || variant.variantImage || undefined,
      variantImages: variant.variantImages || [],
      ...variant
    };
    return baseVariant;
  });
};

const detectVariantHierarchy = (variants: any[]): string[] | undefined => {
  if (!variants || variants.length === 0) return undefined;
  
  const firstVariant = variants[0];
  if (!firstVariant.attributes || typeof firstVariant.attributes !== 'object') {
    return undefined;
  }
  
  if (firstVariant.variantLevels) {
    return Object.keys(firstVariant.variantLevels).map((key: string) => {
      const match = key.match(/level(\d+)/i);
      if (match) {
        return firstVariant.variantLevels[key];
      }
      return firstVariant.variantLevels[key];
    });
  }
  
  const attributeKeys = Object.keys(firstVariant.attributes);
  if (attributeKeys.length > 0 && attributeKeys.length <= 3) {
    return attributeKeys;
  }
  
  return undefined;
};

const BestSellers: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [products, setProducts] = useState<BestSellerProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalProduct, setModalProduct] = useState<BestSellerProduct | null>(null);
  const navigateToProduct = useProductNavigation();
  const { addToCart } = useCart();
  const { addItem: addToWishlist, removeItem: removeFromWishlist, isItemInWishlist } = useWishlist();
  const lastWishlistActionRef = useRef<number>(0);
  
  // Slider/Scroll states
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [velocity, setVelocity] = useState(0);
  const lastMoveTime = useRef(0);
  const lastMoveX = useRef(0);
  const dragDistanceRef = useRef(0);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchBestSellers();
  }, []);

  const fetchBestSellers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await bestSellerService.getBestSellers();
            
      if (response && response.success && response.data) {
        setProducts(response.data);
      } else {
        const errorMsg = response?.message || 'Failed to load best sellers';
        setError(errorMsg);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An error occurred while fetching best sellers';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product: BestSellerProduct) => {
    try {
      let imgSrc = product.imageUrl;
      if (!imgSrc && Array.isArray((product as any).images) && (product as any).images.length > 0 && (product as any).images[0].url) {
        imgSrc = (product as any).images[0].url;
      }
      
      const cartProduct = {
        id: product._id,
        _id: product._id,
        title: product.title || 'Unnamed Product',
        name: product.title || 'Unnamed Product',
        slug: product.slug,
        price: product.price || 0,
        imageUrl: imgSrc || '',
        description: product.description || ''
      };
      
      addToCart(cartProduct, 1);
      toast.success(`${product.title || 'Item'} added to cart!`, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      toast.error('Failed to add item to cart', {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  const handleMoreInfo = (product: BestSellerProduct) => {
    // Only navigate if drag distance is less than 5px (not scrolling)
    if (product.slug && dragDistanceRef.current < 5) {
      navigateToProduct(product.slug);
    }
  };

  const handleWishlistToggle = (product: BestSellerProduct) => {
    const now = Date.now();
    if (now - lastWishlistActionRef.current < 300) return;
    lastWishlistActionRef.current = now;

    try {
      const isWishlisted = isItemInWishlist(product._id);
      
      if (isWishlisted) {
        removeFromWishlist(product._id);
      } else {
        let primaryImage = product.imageUrl;
        if (!primaryImage && Array.isArray((product as any).images) && (product as any).images.length > 0 && (product as any).images[0].url) {
          primaryImage = (product as any).images[0].url;
        }
        
        const productForWishlist = {
          id: product._id,
          _id: product._id,
          title: product.title || 'Unnamed Product',
          slug: product.slug,
          description: product.description || '',
          price: product.price || 0,
          compareAtPrice: product.compareAtPrice ?? null,
          quantity: product.quantity ?? 0,
          images: primaryImage ? [primaryImage] : [],
          isActive: product.isActive,
          isFeatured: product.isFeatured ?? false,
          hasVariants: Array.isArray(product.variants) && product.variants.length > 0,
          categories: (product.categories || []).map(c => c.name),
          tags: product.tags || []
        } as any;

        addToWishlist(productForWishlist);
      }
    } catch (error) {
      console.error('Wishlist error:', error);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const container = sliderRef.current;
    if (container) {
      setIsDragging(true);
      setStartX(e.pageX - container.offsetLeft);
      setScrollLeft(container.scrollLeft);
      setVelocity(0);
      dragDistanceRef.current = 0;
      lastMoveTime.current = Date.now();
      lastMoveX.current = e.pageX;
      container.style.scrollBehavior = 'auto';
      container.classList.add('dragging');
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !sliderRef.current) return;
    e.preventDefault();
    
    const container = sliderRef.current;
    const x = e.pageX - container.offsetLeft;
    const walk = (x - startX) * 2;
    container.scrollLeft = scrollLeft - walk;
    
    // Track total drag distance
    dragDistanceRef.current += Math.abs(e.pageX - lastMoveX.current);
    
    const now = Date.now();
    const dt = now - lastMoveTime.current;
    if (dt > 0) {
      const dx = e.pageX - lastMoveX.current;
      setVelocity(dx / dt);
    }
    lastMoveTime.current = now;
    lastMoveX.current = e.pageX;
  };

  const handleMouseUp = () => {
    const container = sliderRef.current;
    if (container) {
      container.style.scrollBehavior = 'smooth';
      container.classList.remove('dragging');
      
      if (Math.abs(velocity) > 0.5) {
        const momentumScroll = velocity * 100;
        container.scrollLeft -= momentumScroll;
      }
    }
    
    // Small delay to ensure drag distance is captured
    setTimeout(() => {
      setIsDragging(false);
      // Reset drag distance after a bit longer to allow click to process
      setTimeout(() => {
        dragDistanceRef.current = 0;
      }, 50);
    }, 50);
    
    setVelocity(0);
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      handleMouseUp();
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const container = sliderRef.current;
    if (container) {
      setIsDragging(true);
      setStartX(e.touches[0].pageX - container.offsetLeft);
      setScrollLeft(container.scrollLeft);
      setVelocity(0);
      dragDistanceRef.current = 0;
      lastMoveTime.current = Date.now();
      lastMoveX.current = e.touches[0].pageX;
      container.style.scrollBehavior = 'auto';
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !sliderRef.current) return;
    
    const container = sliderRef.current;
    const x = e.touches[0].pageX - container.offsetLeft;
    const walk = (x - startX) * 2;
    container.scrollLeft = scrollLeft - walk;
    
    // Track total drag distance
    dragDistanceRef.current += Math.abs(e.touches[0].pageX - lastMoveX.current);
    
    const now = Date.now();
    const dt = now - lastMoveTime.current;
    if (dt > 0) {
      const dx = e.touches[0].pageX - lastMoveX.current;
      setVelocity(dx / dt);
    }
    lastMoveTime.current = now;
    lastMoveX.current = e.touches[0].pageX;
  };

  const handleTouchEnd = () => {
    const container = sliderRef.current;
    if (container) {
      container.style.scrollBehavior = 'smooth';
      
      if (Math.abs(velocity) > 0.5) {
        const momentumScroll = velocity * 100;
        container.scrollLeft -= momentumScroll;
      }
    }
    
    setTimeout(() => {
      setIsDragging(false);
      setTimeout(() => {
        dragDistanceRef.current = 0;
      }, 50);
    }, 50);
    
    setVelocity(0);
  };

  // Memoize modal data to prevent prop changes on every render
  const modalData = useMemo(() => {
    if (!modalProduct) return null;
    return {
      variants: modalProduct.variants ? convertVariantsForDisplay(modalProduct) : [],
      hierarchicalVariants: modalProduct.variants,
      variantHierarchy: (modalProduct as any).variantHierarchy || detectVariantHierarchy(modalProduct.variants || [])
    };
  }, [modalProduct]);

  if (isMobile) {
    return <BestSellersMobile />;
  }

  if (loading) {
    return (
      <div className="best-sellers-section" style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="best-sellers-section">
        <div className="error-message">
          {error}
          <button onClick={fetchBestSellers} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="best-sellers-section">
      <div className="best-sellers-container">
        <h2 className="best-sellers-title">Best Sellers</h2>
        <div 
          ref={sliderRef}
          className="bs-products-row"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
            WebkitOverflowScrolling: 'touch',
            scrollSnapType: 'x mandatory',
            scrollBehavior: 'smooth',
            overflowX: 'auto',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none'
          }}
        >
          {products.map((product) => {
            let imgSrc = product.imageUrl;
            if (!imgSrc && Array.isArray((product as any).images) && (product as any).images.length > 0 && (product as any).images[0].url) {
              imgSrc = (product as any).images[0].url;
            }
            if (!imgSrc) {
              imgSrc = 'https://ik.imagekit.io/raghav11/download.jpg?updatedAt=1757934662868';
            }
            
            return (
              <ProductCard
                key={product._id}
                id={product._id}
                title={product.title}
                price={`₹${Math.round(product.price || 0)}`}
                image={imgSrc}
                slug={product.slug}
                deliveryTag={true}
                onAddToCart={() => handleAddToCart(product)}
                onAddToWishlist={() => handleWishlistToggle(product)}
                onViewOptions={() => setModalProduct(product)}
                isWishlisted={isItemInWishlist(product._id)}
                onMoreInfo={() => handleMoreInfo(product)}
                quantity={product.quantity}
                variants={product.variants}
                hierarchicalVariants={product.variants}
                variantHierarchy={(product as any).variantHierarchy}
              />
            );
          })}
        </div>
      </div>
      {/* Variant Options Modal */}
      {modalProduct && modalData && (
        isMobile ? (
          <VariantOptionModalMobile
            isOpen={!!modalProduct}
            onClose={() => setModalProduct(null)}
            product={modalProduct}
            variants={modalData.variants}
            hierarchicalVariants={modalData.hierarchicalVariants}
            variantHierarchy={modalData.variantHierarchy}
          />
        ) : (
          <VariantOptionsModal
            isOpen={!!modalProduct}
            onClose={() => setModalProduct(null)}
            title={modalProduct ? (modalProduct.title || 'Product Options') : ''}
          >
            <VariantSheetDesktop
              product={modalProduct}
              variants={modalData.variants}
              hierarchicalVariants={modalData.hierarchicalVariants}
              variantHierarchy={modalData.variantHierarchy}
              onVariantSelect={() => {}}
            />
          </VariantOptionsModal>
        )
      )}
    </div>
  );
};

export default BestSellers;